import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from "recharts";
import { axisBound, chartValues } from "../../utils/format.js";
import { styles } from "../../styles/styles.js";

const fontFamily =
  "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace";

const tickStyle = {
  fill: "white",
  fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
  fontFamily,
};

function HorizontalBarChart({ shortNames, rawData, names, onClick, mods }) {
  // axisBound ignores non-numbers and returns null when nothing is known.
  //
  // This guard is load-bearing, not cosmetic. The old `Math.max(...rawData)`
  // returned Infinity for an Infinity input (reachable from a zero-denominator
  // cover rate) and -Infinity for an all-missing array, and the tick loop below
  // is `for (t = 0; t <= maxValue; t += step)` — which never terminates on
  // Infinity. That is a hung browser tab, not a wrong number.
  const maxValue = axisBound(rawData ?? [], 10);

  if (maxValue === null) {
    return (
      <div style={{ ...styles.unknown, height: 250, display: "flex", alignItems: "center", justifyContent: "center" }}>
        No data for this comparison
      </div>
    );
  }

  const defaultMods = {
    xAxis: { min: -maxValue, max: maxValue },
  };

  const finalMods = {
    ...defaultMods,
    xAxis: { ...defaultMods.xAxis, ...(mods?.xAxis || {}) },
  };

  // Unknown entries become null rather than 0. Recharts renders null as a gap
  // and leaves it out of the domain; a 0 would draw a real bar at the origin,
  // which reads as "we measured this and it came out dead even".
  const chartData = chartValues(rawData).map((val, i) => ({
    name: names[i],
    value: val,
  }));

  // Build symmetric ticks around 0 with step of 10
  const step = 10;
  const xTicks = [];
  for (let t = 0; t <= maxValue; t += step) {
    xTicks.push(t);
    if (t !== 0) xTicks.push(-t);
  }
  xTicks.sort((a, b) => a - b);

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart
        data={chartData}
        layout="vertical"
        onClick={onClick}
        margin={{ left: 0, right: 10, top: 0, bottom: 30 }}
      >
        <CartesianGrid
          stroke="rgba(255, 255, 255, 0.2)"
          horizontal={false}
          vertical={true}
        />
        <XAxis
          type="number"
          domain={[finalMods.xAxis.min, finalMods.xAxis.max]}
          ticks={xTicks}
          tick={{ style: tickStyle }}
          tickFormatter={(v) => Math.round(v)}
          stroke="white"
        />
        <YAxis
          type="category"
          dataKey="name"
          tick={{ style: { ...tickStyle, fontSize: 12 } }}
          axisLine={false}
          width={80}
        />
        <ReferenceLine x={0} stroke="white" strokeWidth={1} />
        <Bar dataKey="value" barSize={30}>
          {chartData.map((entry, index) => (
            <Cell key={index} fill="white" />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default HorizontalBarChart;
